import {
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
} from "@backstage/theme";

// Utility functions to lighten and darken colors
const lightenColor = (color: string, amount: number): string => {
  try {
    // Remove the # if it exists
    let hex = color.replace('#', '');
    
    // Parse the hex values to RGB
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);
    
    // Lighten each component
    r = Math.min(255, Math.floor(r + (255 - r) * amount));
    g = Math.min(255, Math.floor(g + (255 - g) * amount));
    b = Math.min(255, Math.floor(b + (255 - b) * amount));
    
    // Convert back to hex
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  } catch (e) {
    console.error('Error lightening color:', e);
    return color;
  }
};

const darkenColor = (color: string, amount: number): string => {
  try {
    // Remove the # if it exists
    let hex = color.replace('#', '');
    
    // Parse the hex values to RGB
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);
    
    // Darken each component
    r = Math.max(0, Math.floor(r * (1 - amount)));
    g = Math.max(0, Math.floor(g * (1 - amount)));
    b = Math.max(0, Math.floor(b * (1 - amount)));
    
    // Convert back to hex
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  } catch (e) {
    console.error('Error darkening color:', e);
    return color;
  }
};

// Banco Pichincha Color Palette
// Define color constants for reuse throughout the theme
const colors = {
  // Primary colors
  yellow: {
    main: "#FFDD00",     // Pichincha primary yellow
    light: "#FFEA80",    // Lighter variant
    dark: "#F0C000",     // Darker variant for hover states
  },
  blue: {
    main: "#0F265C",     // Pichincha primary blue
    light: "#324b82",    // Lighter variant
    dark: "#081537",     // Darker variant
  },
  // Supplementary colors
  white: "#FFFFFF",
  lightGrey: "#F8F8F8",
  mediumGrey: "#E5E5E5",
  darkGrey: "#656565",
  // Status colors
  error: "#D32F2F",
  warning: "#ED6C02",
  success: "#2E7D32",
  info: "#0288D1",
};

// Custom theme for Banco Pichincha
export const customTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      primary: {
        main: colors.yellow.main,
        light: colors.yellow.light,
        dark: colors.yellow.dark,
        contrastText: colors.blue.main,
      },
      secondary: {
        main: colors.blue.main,
        light: colors.blue.light,
        dark: colors.blue.dark,
        contrastText: colors.white,
      },
      error: {
        main: colors.error,
        light: lightenColor(colors.error, 0.2),
        dark: darkenColor(colors.error, 0.2),
      },
      warning: {
        main: colors.warning,
        light: lightenColor(colors.warning, 0.2),
        dark: darkenColor(colors.warning, 0.2),
      },
      info: {
        main: colors.info,
        light: lightenColor(colors.info, 0.2),
        dark: darkenColor(colors.info, 0.2),
      },
      success: {
        main: colors.success,
        light: lightenColor(colors.success, 0.2),
        dark: darkenColor(colors.success, 0.2),
      },
      text: {
        primary: colors.blue.main,
        secondary: colors.darkGrey,
        disabled: 'rgba(0, 0, 0, 0.38)',
        hint: 'rgba(0, 0, 0, 0.38)',
      },
      background: {
        default: "#F5F5F5",
        paper: colors.white,
      },
      navigation: {
        background: colors.white,
        indicator: colors.yellow.main,
        color: colors.blue.main,
        selectedColor: colors.blue.main,
        submenu: { background: colors.lightGrey },
        navItem: { hoverBackground: colors.mediumGrey }
      },
      action: {
        active: colors.blue.main,
        hover: colors.lightGrey,
        selected: colors.yellow.light,
        disabled: colors.mediumGrey,
        disabledBackground: colors.lightGrey,
      },
      divider: colors.mediumGrey,
    },
  }),
  defaultPageTheme: "home",
  fontFamily: '"Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
  /* Page themes for different page types */
  pageTheme: {
    home:          genPageTheme({ colors: [colors.yellow.main, colors.blue.main], shape: shapes.wave }),
    documentation: genPageTheme({ colors: [colors.blue.main, colors.yellow.main], shape: shapes.wave2 }),
    tool:          genPageTheme({ colors: [colors.blue.main], shape: shapes.round }),
    service:       genPageTheme({ colors: [colors.blue.main], shape: shapes.wave }),
    website:       genPageTheme({ colors: [colors.yellow.main], shape: shapes.wave }),
    library:       genPageTheme({ colors: [colors.blue.main], shape: shapes.wave }),
    other:         genPageTheme({ colors: [colors.blue.main], shape: shapes.round }),
    app:           genPageTheme({ colors: [colors.yellow.main, colors.blue.main], shape: shapes.wave }),
    apis:          genPageTheme({ colors: [colors.blue.main], shape: shapes.wave }),
  },
  components: {
    // Backstage components overrides - Header blanco
    BackstageHeader: {
      styleOverrides: {
        header: {
          background: colors.white,
          backgroundColor: colors.white,
          backgroundImage: 'none',
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)',
        },
        title: {
          color: colors.blue.main,
        },
        subtitle: {
          color: colors.darkGrey,
        },
        type: {
          color: colors.blue.main,
        },
        leftItemsBox: {
          color: colors.blue.main,
        },
        rightItemsBox: {
          color: colors.blue.main,
        },
      },
    },
    BackstageHeaderLabel: {
      styleOverrides: {
        label: {
          color: colors.darkGrey,
        },
        value: {
          color: colors.blue.main,
        },
      },
    },
    BackstagePage: {
      styleOverrides: {
        root: {
          backgroundColor: colors.lightGrey,
        },
      },
    },
    // Customize Material-UI components
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.white,
          color: colors.blue.main,
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
        colorPrimary: {
          backgroundColor: colors.white,
          color: colors.blue.main,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          textTransform: 'none',
          fontFamily: '"Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.15)',
          },
        },
        containedPrimary: {
          color: colors.blue.main,
          '&:hover': {
            backgroundColor: colors.yellow.dark,
          },
        },
        containedSecondary: {
          color: colors.white,
          '&:hover': {
            backgroundColor: colors.blue.light,
          },
        },
        text: {
          color: colors.blue.main,
          '&:hover': {
            backgroundColor: lightenColor(colors.blue.main, 0.95),
          },
        },
        textPrimary: {
          color: colors.blue.main,
          '&:hover': {
            backgroundColor: lightenColor(colors.blue.main, 0.95),
          },
        },
        outlined: {
          borderColor: colors.blue.main,
          color: colors.blue.main,
          '&:hover': {
            borderColor: colors.blue.dark,
            backgroundColor: lightenColor(colors.blue.main, 0.95),
          },
        },
        outlinedPrimary: {
          borderColor: colors.blue.main,
          color: colors.blue.main,
          '&:hover': {
            borderColor: colors.blue.dark,
            backgroundColor: lightenColor(colors.blue.main, 0.95),
          },
        },
      },
    },
    // Estilos para las tarjetas
    MuiCard: {
      styleOverrides: {
        root: {
          display: 'flex',
          flexDirection: 'column',
          // height: '100%',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(15, 38, 92, 0.1)',
          border: '1px solid rgba(15, 38, 92, 0.05)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(15, 38, 92, 0.15)',
            transform: 'translateY(-2px)',
            borderColor: 'rgba(15, 38, 92, 0.1)',
          },
          '&:focus': {
            outline: 'none',
            boxShadow: '0 0 0 3px rgba(255, 221, 0, 0.4), 0 6px 16px rgba(15, 38, 92, 0.15)',
          },
          // Sidebar filters del Scaffolder - más compacto
          '&[class*="CatalogReactUserListPicker-root"]': {
            height: 'auto',
          },
        },
      },
    },
    MuiCardMedia: {
      styleOverrides: {
        root: {
          '&.card-header-pichincha': {
            background: colors.yellow.main,
            color: colors.blue.main,
            padding: '20px',
            '& .MuiTypography-h6': {
              fontWeight: 'bold',
              color: colors.blue.main,
              marginBottom: '4px',
            },
            '& .MuiTypography-subtitle2': {
              color: colors.blue.main,
            },
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '&.card-content-pichincha': {
            flexGrow: 1,
            padding: '24px 16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          },
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          justifyContent: 'center',
          padding: '16px',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: colors.lightGrey,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          color: colors.blue.main,
          fontWeight: 'bold',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          borderRadius: '50%',
          border: `3px solid ${colors.yellow.main}`,
        },
        img: {
          borderRadius: '50%',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.white,
          borderRight: `1px solid ${colors.mediumGrey}`,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: colors.yellow.light,
            borderLeft: `3px solid ${colors.yellow.main}`,
          },
          '&:hover': {
            backgroundColor: colors.lightGrey,
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: colors.blue.main,
          '&:hover': {
            color: colors.blue.dark,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: colors.darkGrey,
          '&.Mui-focused': {
            color: `${colors.blue.main} !important`,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: colors.darkGrey,
          '&.Mui-focused': {
            color: `${colors.blue.main} !important`,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: `${colors.blue.main} !important`,
          },
        },
      },
    },
    BackstageIconLinkVertical: {
      styleOverrides: {
        primary: {
          color: colors.blue.main,
          '&:hover': {
            color: colors.blue.dark,
          },
        },
      },
    },
  },
});
